// What to call a county-level place. Most are "X County", but Louisiana has parishes,
// Virginia (and Baltimore, St. Louis and Carson City) has independent cities that belong
// to no county, and D.C. is one place. The site used to append " County" to all of them,
// which gave it "Orleans County" and "Alexandria County".

/** States whose county-equivalents include independent cities, coded 500 and up. */
const INDEPENDENT_CITY_STATES = new Set(["VA", "MD", "MO", "NV"]);

export interface CountyNameParts {
  name: string;
  fips: string;
  stateAbbr: string;
}

export function isIndependentCity(c: CountyNameParts): boolean {
  return INDEPENDENT_CITY_STATES.has(c.stateAbbr) && Number(c.fips.slice(2)) >= 500;
}

/**
 * "Fulton County", "Orleans Parish", "City of Alexandria", "Carson City",
 * "District of Columbia".
 */
export function countyLabel(c: CountyNameParts): string {
  if (c.stateAbbr === "DC") return c.name;
  if (c.stateAbbr === "LA") return `${c.name} Parish`;
  if (isIndependentCity(c)) {
    if (/\bCity\b/.test(c.name)) return c.name; // "Carson City"
    return `City of ${c.name.replace(/\s+city$/i, "")}`;
  }
  // Alaska's boroughs and census areas have no single suffix; the bare name is right.
  if (c.stateAbbr === "AK") return c.name;
  return `${c.name} County`;
}

/** The word for a county in this state, for headings such as "Find your parish". */
export function countyNoun(stateAbbr: string, plural = false): string {
  if (stateAbbr === "LA") return plural ? "parishes" : "parish";
  return plural ? "counties" : "county";
}

/**
 * The word for all of a state's county-level places, for counts such as "70 of
 * Virginia's 133 ...". Virginia's 133 are 95 counties and 38 independent cities, so
 * "133 counties" would be wrong; Maryland, Missouri and Nevada each have one such city.
 */
export function countyNounForCount(stateAbbr: string): string {
  if (INDEPENDENT_CITY_STATES.has(stateAbbr)) return "counties and cities";
  if (stateAbbr === "AK") return "boroughs and census areas";
  return countyNoun(stateAbbr, true);
}
